<template lang="pug">
div.hero-page(
    v-bind:class="className"
    v-bind:style="style")

    div.infobox(
        ref="infoBox"
        v-if="$slots['infobox'] && !infoBoxHoldout"
        v-bind:style="infoBoxStyle")
        slot(name="infobox" v-bind:item="spotlight")

    div.infobox(
        ref="infoBox"
        v-else-if="spotlight && !infoBoxHoldout"
        v-bind:style="infoBoxStyle")

        h2.title {{ spotlight.Name }}
        div.ingress-text(v-if="spotlight.IngressText" v-html="spotlight.IngressText")

    //- preload next background image to stop flickering
    img.preload-image(
        v-if="nextImageSrc"
        v-bind:src="nextImageSrc")

</template>

<script>
/**
 * Fullsize hero image & infobox for Home front-pages loop.
 */
export default {
    name: 'home-hero-page',

    props: {
        spotlight: {    type: Object },
        resourceType: { type: String }, // used to set css classname
        nextItem: {     type: Object } // to preload
    },

    data: function () {
        return {
            imageInfo: null,
            imageSrc: null,
            nextImageSrc: null,
            style: '',
            className: '',
            infoBoxStyle: 'display: none;',
            infoBoxHoldout: true,
            infoboxHoldoutTimeout: 500,
            infoBoxHoldoutTimeoutRef: null,
            debouncedUpdate: _.debounce(this.update, 100, {trailing: true})
        };
    },

    created: function () {
        this.update();
    },

    watch: {
        spotlight: function () {
            this.infoBoxHoldout = true;
            if (this.infoBoxHoldoutTimeoutRef) {
                clearTimeout(this.infoBoxHoldoutTimeoutRef);
            }
            this.update(); // update page asap
        }
    },

    methods: {

        /**
         * Hero item should always have CoverImageInfo, IngressImageInfo, or ImageInfo.
         */
        update: function () {
            if (!this.spotlight) {
                return;
            }

            let imageSrc;
            const imageAttrs = {resolution: {x: '100vw', y: '100vh'}, missingImage: 'Redirect'};
            const imageInfo = this.getItemImageInfo(this.spotlight);

            // get background image src
            if (imageInfo && imageInfo.Url) {
                imageSrc = this.$helpers.constructImageSrc2(imageInfo.Url, imageAttrs);
            }

            // get next item image source for preload
            const nextImageInfo = this.getItemImageInfo(this.nextItem);

            // get background image src
            if (nextImageInfo && nextImageInfo.Url) {
                setTimeout(() => {
                    this.nextImageSrc = this.$helpers.constructImageSrc2(nextImageInfo.Url, imageAttrs);
                }, 3000);
            }
            else {
                this.nextImageSrc = null;
            }

            // emit signal to front-page to update background gradient
            // (disabled for performance reasons)
            // this.$emit('palette-change', imageInfo);

            this.infoBoxStyle = this.getInfoBoxStyle(imageInfo);

            // infobox needs to be re-positioned after rendering and waiting for transition-duration
            setTimeout(() => {
                this.fixInfoBoxStyle();
            }, 510); // transition-duration 500ms

            // format style with background-image url
            this.className = this.getClassName();

            if (this.heroIndex === 0) {
                // the first page should show infobox quickly
                this.updatePageStyle(imageSrc, imageInfo);
            }
            else {
                // other pages set style and variables after small timeout
                this.infoBoxHoldoutTimeoutRef = setTimeout(() => { this.updatePageStyle(imageSrc, imageInfo); }, this.infoboxHoldoutTimeout);
            }
        },

        /**
         * Get preferred image info for the background.
         */
        getItemImageInfo: function (item) {
            if (item && item.CoverImageInfo) {
                return item.CoverImageInfo;
            }
            else if (item && item.IngressImageInfo) {
                return item.IngressImageInfo;
            }
            else if (item && item.ImageInfo) {
                return item.ImageInfo;
            }
        },

        updatePageStyle: function (imageSrc, imageInfo) {
            const style = this.getStyle(imageSrc, imageInfo);

            // set image width & height
            // NOTE: returning from auth0 login screen with back (instead of logging in)
            //       will distort the image, because window.innerHeight gives a wrong value.
            // style += `width: ${window.innerWidth}px;`;
            // style += `height: ${window.innerHeight}px;`;

            this.style = style;
            this.imageSrc = imageSrc;
            this.imageInfo = imageInfo;
            this.updateInfoboxStyle(imageInfo);
        },

        /**
         * In order to prevent infobox flicker during transition (to appear on correct background),
         * maintain a small time window when infobox is not visible.
         */
        updateInfoboxStyle: function (imageInfo) {
            this.infoBoxHoldout = false;
            this.infoBoxStyle = this.getInfoBoxStyle(imageInfo);
            this.$nextTick(this.fixInfoBoxStyle);
        },

        getClassName: function () {
            return this.spotlight && `hero-${this.resourceType || 'intro'}`;
        },

        /**
         * Spotlight should contain all the information to construct the page
         * with background, infobox and perhaps suggestion cards.
         */
        getStyle: function (imageSrc, imageInfo) {
            return this.$helpers.getBackgroundImageStyle(imageSrc, null, imageInfo);
        },

        /**
         * Render infobox in slightly different positions.
         */
        getInfoBoxStyle: function (imageInfo) {
            const { x, y } = this.getInfoBoxCoordinates(imageInfo);

            let style = '';
            style += `transform: translateX(${x}vw) translateY(${y}vh);`;
            // add duration here, so initially it snaps to the correct position
            style += 'transition-duration: 500ms;';

            return style;
        },

        /**
         * After infobox has been rendered, it may be partially out of view.
         * Its size and position are compared to client screen and moved with marginLeft
         * to adjust the original positioning done with transform.
         */
        fixInfoBoxStyle: function (imageInfo) {
            const infoBox = this.$refs.infoBox;
            const infoBoxRect = infoBox && infoBox.getBoundingClientRect();

            if (!infoBoxRect) {
                return;
            }

            // get position and size
            const { left, right, top } = infoBoxRect;
            // get infobox card height (infobox height is not reliable)
            const card = infoBox.querySelector('.card');
            const cardRect = card && card.getBoundingClientRect();
            const cardHeight = cardRect && cardRect.height;

            const transformStyle = infoBox.style.transform;
            let match = transformStyle.match(/translateX\(([-\d]+)vw/);
            let translateX = match && parseInt(match[1], 10);
            match = transformStyle.match(/translateY\(([-\d]+)vh/);
            let translateY = match && parseInt(match[1], 10);

            // hero container, user might have scrolled down
            const heroRect = this.$el.getBoundingClientRect();
            const heroBottom = heroRect.bottom;

            const maxRight = window.innerWidth - 45;
            const maxBottom = heroBottom - 45;

            // infobox overflow in known in pixel amount.
            // calculate how many pixels 1 vw and vh are, and find how much the translate needs to be adjusted
            const vwDepth = 100 / window.innerWidth;
            const vhDepth = 100 / window.innerHeight;
            if (right > maxRight) {
                const xOverflow = right - maxRight;
                translateX -= Math.round(xOverflow * vwDepth);
                // console.log("infobox overflows to the RIGHT by", xOverflow, right);
            }
            // to be sure, check also left and top
            else if (left < 0) {
                const xOverflow = (left - 10);
                translateX -= Math.round(xOverflow * vwDepth);
                // console.log("infobox overflows to the LEFT by", xOverflow, left);
            }

            if (heroBottom > maxBottom) {
                const yOverflow = heroBottom - maxBottom;
                translateY -= Math.round(yOverflow * vhDepth);
                // console.log("infobox overflows to the BOTTOM by", yOverflow, top + cardHeight);
            }
            else if (cardHeight && (top + cardHeight) > maxBottom) {
                const yOverflow = top + cardHeight - maxBottom;
                translateY -= Math.round(yOverflow * vhDepth);
                // console.log("infobox overflows to the BOTTOM by", yOverflow, top + cardHeight);
            }
            else if (top < 110) {
                const yOverflow = (top - 240);
                translateY -= Math.round(yOverflow * vwDepth);
                // console.log("infobox overflows to the TOP by", yOverflow, top);
            }

            // update infobox transform
            let transform = '';
            if (translateX) {
                const x = _.clamp(translateX, 4, 90);
                transform += `translateX(${x}vw) `;
            }
            if (translateY) {
                const y = _.clamp(translateY, 5, 90);
                transform += `translateY(${y}vh)`;
            }
            if (transform) {
                infoBox.style.transform = transform;
            }
        },

        /**
         * Get { x, y } coordinates, value between 0 - 100 of percentage of viewport width / height.
         * If the given `ImageInfo` has `FocusPoint`, it will be accounted for.
         * The screen is thought of as four quadrants into which the focus will fall.
         * FocusPoint 0, 0 is the center, -50 left/top and +50 right/bottom.
         *
         * The box might sometimes be partially out of view. To fix this, the box first is rendered
         * and then its position is adjusted by checking its actual bounding box.
         *
         */
        getInfoBoxCoordinates: function (imageInfo) {
            let xRange = [0, 25];
            let yRange = [0, 50];
            const isMobile = process.client && window.innerWidth < 600;

            // smaller screens!
            if (isMobile) {
                xRange = [1];
                yRange = [8, 20];
            }
            if (imageInfo && imageInfo.FocusPoint) {
                const focusX = imageInfo.FocusPoint[0];
                const focusY = imageInfo.FocusPoint[1];

                // upper left
                if (focusX < 0 && focusY < 0) {
                    // console.log("Focus UPPER LEFT");
                    if (!isMobile) {
                        xRange = [20, 25];
                        yRange = [50, 55];
                    }
                    else {
                        yRange = [14, 20];
                    }
                }

                // upper right
                else if (focusX > 0 && focusY < 0) {
                    // console.log("Focus UPPER RIGHT");
                    if (!isMobile) {
                        xRange = [0, 5];
                        yRange = [45, 50];
                    }
                    else {
                        yRange = [14, 20];
                    }
                }

                // lower left
                else if (focusX < 0 && focusY > 0) {
                    // console.log("Focus LOWER LEFT");
                    if (!isMobile) {
                        xRange = [5, 25];
                        yRange = [5, 15];
                    }
                }

                // lower right
                else if (focusX > 0 && focusY > 0) {
                    // console.log("Focus LOWER RIGHT");
                    if (!isMobile) {
                        xRange = [0, 0];
                        yRange = [5, 15];
                    }
                }
            }

            const x = _.sample(xRange);
            const y = _.sample(yRange);

            return { x, y };
        }
    }
};
</script>

<style lang="scss">
// @import '~assets/styles/variables';

.hero-page {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    display: flex;
    flex-direction: row;
    align-items: left;
    width: 100vw;
    // height: calc(100vh - 108px); // navbar height ~108px
    height: 100vh; // with transparent navbar position: absolute
    margin: 0;
    padding: 110px $main-content-margin 0;
    text-align: center;
    vertical-align: middle;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: 50%;
    // background image transition effect
    transition-property: background-image, background-position;
    transition-duration: 1s;
    transition-timing-function: ease-in-out;
    &.is-fullheight {
        position: absolute;
        background-attachment: fixed;
        background-size: cover;
        background-position: center center;
    }
    .contents {
        justify-content: space-between;
        width: 100%;
    }
    .segment {
        max-width: $main-content-max-width;
    }
    .card {
        .card-image {
            // hide card image
            display: none;
        }
        .card-header {
            background: rgba(239, 239, 239, 0.75);
        }
    }
    .btn {
        display: inline-flex;
        align-content: center;
        text-align: middle;
        span {
            align-self: center;
        }
    }
    .infobox {
        position: static;
        top: 0;
        left: 0;
        width: auto;
        min-width: 460px;
        max-width: 75vw;
        margin-right: auto;
        margin-left: auto;
        transform: translateX(120vw) translateY(50vh); // initially position outside the screen
        transition-property: transform, opacity;
        transition-duration: 240ms; // will be reset in script, this is for initial infobox
        transition-timing-function: ease-in-out;
        h4 {
            margin: 1.5rem auto 2rem;
            font-size: 1.5rem;
        }
        em {
            font-size: 1.5rem;
        }
        .buttons {
            display: inline-flex;
            align-items: center;
            justify-items: center;
        }
        button.btn {
            margin: 2rem 0.5rem 0.5rem !important;
            padding: 10px 0.8rem !important;
            font-family: $helvetica-bold;
            color: $dark-gray-2;
            text-transform: uppercase;
            background: $light-gray;
        }
    }
    .preload-image {
        z-index: -1;
        display: none;
    }

    @media screen and (max-width: $breakpoint-mobile - 1) {
        padding-right: 0;
        padding-left: 0;
        .segment {
            max-width: $mobile-content-max-width;
            margin-top: 0;
        }
        .infobox {
            min-width: unset;
            max-width: 98vw;
        }
    }
}
</style>
